import React, { useState } from "react";
import { Platform } from "..";
import { Actions, ActionsContainer } from "Components/Modal";
import { TextInput } from "melodies-source/TextInput";
import { Body1 } from "melodies-source/Text";
import styled from "styled-components";
import { ModalHeaderTitle } from "melodies-source/Modal";
import { Radio } from "melodies-source/Selectable";
import { Textarea, HTMLTextearea } from "melodies-source/Textarea";
import { StackedModal } from "melodies-source/StackedModal";
import { Button } from "melodies-source/Button";

interface Props {
  platform: Platform;
  isOpen: boolean;
  onClose: () => void;
}

export const ModalSave: React.FC<Props> = ({ platform, isOpen, onClose }) => {
  const [integrationType, setIntegrationType] = useState<"id" | "code">("id");
  const [id, setId] = useState(platform.value);
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState<{ id?: string; code?: string }>({});

  const header = (
    <Header>
      {platform.logo}
      <ModalHeaderTitle>{platform.name}</ModalHeaderTitle>
    </Header>
  );

  const handleSave = () => {
    // if integration is pasted code, parse and extract the id
    if (integrationType === "code") {
      if (!code.trim()) {
        setErrors((errs) => ({ ...errs, code: "This field can not be empty" }));
        return;
      }

      const platformId = platform.extractId(code);
      if (platformId) {
        platform.setValue(platformId, "id");
        setId(platformId);
        onClose();
        setIntegrationType("id");
        return;
      }

      setErrors((errs) => ({
        ...errs,
        code: `Could not find your ${platform.name} id in the pasted code.`,
      }));
      return;
    }

    if (!id.trim()) {
      setErrors((errs) => ({ ...errs, id: "This field can not be empty" }));
      return;
    }

    platform.setValue(id, integrationType);
    return onClose();
  };

  return (
    <StackedModal
      variant="medium"
      isOpen={isOpen}
      header={header}
      onClose={onClose}
    >
      <Info>
        Paste your id or code below. Delete to remove it from your site.
      </Info>
      <RadioGroup>
        <Radio
          value={integrationType === "id"}
          onChange={(v) => v && setIntegrationType("id")}
          label="I have the ID"
        />
        <Radio
          value={integrationType === "code"}
          onChange={(v) => v && setIntegrationType("code")}
          label="I have the code"
        />
      </RadioGroup>
      {integrationType === "id" && (
        <TextInput
          value={id}
          hasError={!!errors.id}
          helperText={errors.id}
          onChange={(text) => {
            setErrors((errs) => ({ ...errs, id: "" }));
            setId(text);
          }}
          label={platform.inputLabel.id}
          placeholder={platform.placeholder.id}
        />
      )}
      {integrationType === "code" && (
        <CodeInput
          rows={11}
          hasError={!!errors.code}
          helperText={errors.code}
          onChange={(text) => {
            setErrors((errs) => ({ ...errs, code: "" }));
            setCode(text);
          }}
          value={code}
          label={platform.inputLabel.code}
          placeholder={platform.placeholder.code}
        />
      )}
      <ActionsContainer>
        <Actions>
          <Button text onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave}>
            {platform.value ? "Save" : "Add"}
          </Button>
        </Actions>
      </ActionsContainer>
    </StackedModal>
  );
};

const Header = styled.div`
  --header-text-color: var(--main-color);

  display: flex;
  align-items: center;
  gap: 20px;

  svg,
  img {
    width: 50px;
  }
`;

const Info = styled(Body1)`
  margin-bottom: 15px;
`;

const RadioGroup = styled.div`
  margin-bottom: 21px;
`;

const CodeInput = styled(Textarea)`
  ${HTMLTextearea} {
    height: 100%;
  }
`;
